import { Fragment, useCallback, useContext, useEffect, useState } from "react";
import { Map, Marker, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import {
  Autocomplete,
  CircularProgress,
  ClickAwayListener,
  InputAdornment,
  Popper,
  TextField,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getCustomerAddress,
  // getDistanceOutlet,
  setCustomerAddress,
} from "../../services/purchaseApi";
import { GlobalStateContext } from "../../components/context/GlobalStateContext";
import { useFormik } from "formik";
import {
  getCity,
  getDistrict,
  getProvince,
  getSubdistrict,
} from "../../services/authApi";
import useDebounce from "../../hooks/useDebounce";
import { Search } from "@mui/icons-material";
import LoadingBackdrop from "../common/LoadingBackdrop";
import ErrorPopup from "../../components/common/ErrorPopup";

const INITIAL_LAT = -6.175;
const INITIAL_LNG = 106.8266;
const INITIAL_ZOOM = 15;

const ChangeAddress = () => {
  const map = useMap("google-map");
  const placesLibrary = useMapsLibrary("places");
  const companyId = localStorage.getItem("companyId");
  const { isLogin } = useContext(GlobalStateContext);

  const [errorMessage, setErrorMessage] = useState("");
  const [search, setSearch] = useState("");
  const [placesService, setPlacesService] = useState(null);
  const [autocompleteService, setAutocompleteService] = useState(null);
  const [results, setResults] = useState([]);
  const [anchorElSearch, setAnchorElSearch] = useState(null);
  const [coordinates, setCoordinates] = useState([0, 0]);
  const [latLng, setLatLng] = useState(null);
  const [cameraProps, setCameraProps] = useState({
    center: { lat: INITIAL_LAT, lng: INITIAL_LNG },
    zoom: INITIAL_ZOOM,
  });

  // const handleSaveAddress = () => {
  //   if (Object.values(formik.values).includes("")) {
  //     setErrorMessage("Gagal menyimpan, alamat belum lengkap");
  //   }

  //   const body = {
  //     ...formik.values,
  //     latlong: coordinates.join(","),
  //   };

  //   mutateAddress(body);
  // };

  // const { mutate: mutateAddress, isLoading: isLoadingSetAddress } = useMutation(
  //   {
  //     mutationFn: setCustomerAddress,
  //     onSuccess: () => {
  //       mutateDistance({
  //         latlong: coordinates.join(","),
  //         company_id: Number(companyId),
  //       });
  //       refetchAddress();
  //       setErrorMessage("Alamat berhasil diubah");
  //     },
  //     onError: (err) =>
  //       setErrorMessage(err.message.id ? err.message.id : err.message),
  //   }
  // );

  // const { mutate: mutateDistance, isLoading: isLoadingDistance } = useMutation({
  //   mutationFn: getDistanceOutlet,
  //   onSuccess: (data) => {
  //     localStorage.setItem("companyId", data.company_id);
  //   },
  //   onError: (err) =>
  //     setErrorMessage(err.message.id ? err.message.id : err.message),
  // });

  const {
    data: dataAddress,
    isFetching: isFetchingAddress,
    refetch: refetchAddress,
  } = useQuery({
    queryKey: ["customer-address"],
    queryFn: () => getCustomerAddress(),
    enabled: isLogin === true,
    keepPreviousData: false,
  });

  const formik = useFormik({
    initialValues: {
      province: dataAddress ? dataAddress.province : "",
      city: dataAddress ? dataAddress.city : "",
      district: dataAddress ? dataAddress.district : "",
      subdistrict: dataAddress ? dataAddress.subdistrict : "",
      postal_code: dataAddress ? dataAddress.postal_code : "",
      detail_address: dataAddress ? dataAddress.detail_address : "",
    },
    enableReinitialize: true,
  });

  const debounceProvince = useDebounce(formik.values.province, 800);
  const debounceCity = useDebounce(formik.values.city, 800);
  const debounceDistrict = useDebounce(formik.values.district, 800);

  map?.addListener("click", (mapsMouseEvent) => {
    const json = mapsMouseEvent.latLng.toJSON();
    setSearch("");
    setLatLng(mapsMouseEvent.latLng);
    setCoordinates([json.lat, json.lng]);
    // formik.setValues({
    //   ...formik.values,
    //   latitude: json.lat,
    //   longitude: json.lng,
    // });

    const geocoder = new window.google.maps.Geocoder();
    geocoder?.geocode({ location: json }, (res, status) => {
      if (status === "OK" && res[0]) {
        formik.setValues({
          ...formik.values,
          province: res[0].address_components.find((el) =>
            el.types.includes("administrative_area_level_1")
          )
            ? res[0].address_components
                .find((el) => el.types.includes("administrative_area_level_1"))
                .long_name.includes("Jakarta")
              ? "DKI Jakarta"
              : res[0].address_components.find((el) =>
                  el.types.includes("administrative_area_level_1")
                )?.long_name
            : "",
          city: removePrefix(
            res[0].address_components.find((el) =>
              el.types.includes("administrative_area_level_2")
            )?.long_name || ""
          ),
          district: removePrefix(
            res[0].address_components.find((el) =>
              el.types.includes("administrative_area_level_3")
            )?.long_name || ""
          ),
          subdistrict: removePrefix(
            res[0].address_components.find((el) =>
              el.types.includes("postal_code")
            )
              ? res[0].address_components.find((el) =>
                  el.types.includes("administrative_area_level_4")
                )?.long_name || ""
              : ""
          ),
          postal_code: res[0].address_components.find((el) =>
            el.types.includes("administrative_area_level_4")
          )
            ? removePrefix(
                res[0].address_components.find((el) =>
                  el.types.includes("postal_code")
                )?.long_name || ""
              )
            : "",
          detail_address: res.vicinity,
        });
      }
    });
  });

  const removePrefix = (placeString) => {
    if (!placeString || placeString === "") return "";

    const excludeString = ["Kota", "Kabupaten", "Kecamatan", "Kelurahan"];
    const splitString = placeString.split(" ").slice(1).join(" ");

    return excludeString.some((excl) => placeString.includes(excl))
      ? splitString
      : placeString;
  };

  const setMarker = async (mark) => {
    placesService.getDetails({ placeId: mark.place_id }, (res) => {
      setCoordinates([
        res.geometry.location.lat(),
        res.geometry.location.lng(),
      ]);
      formik.setValues({
        ...formik.values,
        province: res.address_components.find((el) =>
          el.types.includes("administrative_area_level_1")
        )
          ? res.address_components
              .find((el) => el.types.includes("administrative_area_level_1"))
              .long_name.includes("Jakarta")
            ? "DKI Jakarta"
            : res.address_components.find((el) =>
                el.types.includes("administrative_area_level_1")
              )?.long_name
          : "",
        city: removePrefix(
          res.address_components.find((el) =>
            el.types.includes("administrative_area_level_2")
          )?.long_name || ""
        ),
        district: removePrefix(
          res.address_components.find((el) =>
            el.types.includes("administrative_area_level_3")
          )?.long_name || ""
        ),
        subdistrict: res.address_components.find((el) =>
          el.types.includes("postal_code")
        )
          ? removePrefix(
              res.address_components.find((el) =>
                el.types.includes("administrative_area_level_4")
              )?.long_name || ""
            )
          : "",
        postal_code: res.address_components.find((el) =>
          el.types.includes("administrative_area_level_4")
        )
          ? removePrefix(
              res.address_components.find((el) =>
                el.types.includes("postal_code")
              )?.long_name || ""
            )
          : "",
        detail_address: res.vicinity,
      });

      setLatLng(res.geometry.location);
      setCameraProps({
        center: {
          lat: res.geometry.location.lat(),
          lng: res.geometry.location.lng(),
        },
        zoom: INITIAL_ZOOM,
      });
    });
  };

  const setMarkerByPostalCode = (option) => {
    if (!option) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder?.geocode(
      {
        address: option.kode_pos ? option.kode_pos : option.postal_code,
        region: "ID",
      },
      (results, status) => {
        if (status === "OK" && results[0]) {
          const location = results[0].geometry.location;
          setLatLng(location);
          setCoordinates([location.lat(), location.lng()]);
          setCameraProps({
            center: {
              lat: location.lat(),
              lng: location.lng(),
            },
            zoom: INITIAL_ZOOM,
          });
        }
      }
    );
  };

  useEffect(() => {
    if (dataAddress && dataAddress.postal_code !== "")
      setMarkerByPostalCode(dataAddress);
  }, [dataAddress]);

  const handleCameraChange = useCallback((ev) => {
    setCameraProps(ev.detail);
  });

  const { data: dataProvince, isFetching: isFetchingProvince } = useQuery({
    queryKey: ["province"],
    queryFn: () => getProvince(),
    retryDelay: 60000, // one minute delay
    refetchOnWindowFocus: false,
  });

  const {
    data: dataCity,
    isFetching: isFetchingCity,
    refetch: refetchCity,
  } = useQuery({
    queryKey: ["city", debounceProvince],
    queryFn: () => getCity(formik.values.province),
    retryDelay: 60000, // one minute delay
    refetchOnWindowFocus: false,
  });

  const {
    data: dataDistrict,
    isFetching: isFetchingDistrict,
    refetch: refetchDistrict,
  } = useQuery({
    queryKey: ["district", debounceCity],
    queryFn: () => getDistrict(formik.values.city),
    retryDelay: 60000, // one minute delay
    refetchOnWindowFocus: false,
  });

  const {
    data: dataSubdistrict,
    isFetching: isFetchingSubdistrict,
    refetch: refetchSubdistrict,
  } = useQuery({
    queryKey: ["subdistrict", debounceDistrict],
    queryFn: () => getSubdistrict(formik.values.district),
    retryDelay: 60000, // one minute delay
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!placesLibrary || !map) return;
    setAutocompleteService(new placesLibrary.AutocompleteService(map));
    setPlacesService(new placesLibrary.PlacesService(map));
  }, [placesLibrary, map]);

  useEffect(() => {
    if (!autocompleteService || search.length === 0) {
      setResults([]);
      return;
    }

    autocompleteService.getQueryPredictions({ input: search }, (res) => {
      setResults(res);
    });
  }, [autocompleteService, search]);

  return (
    <div>
      {/* <LoadingBackdrop isLoading={isLoadingDistance || isLoadingSetAddress} /> */}
      <div className="flex flex-col md:flex-row gap-4 items-start">
        <div className="w-full order-last md:order-first">
          <div>
            <p>Provinsi</p>
            <Autocomplete
              fullWidth
              loading={isFetchingProvince}
              disablePortal
              disabled={isFetchingAddress}
              options={dataProvince ? dataProvince : []}
              value={
                formik.values.province !== "" && dataProvince
                  ? dataProvince.find(
                      (prov) => prov.province === formik.values.province
                    )
                  : null
              }
              onChange={(_, option) => {
                formik.setValues({
                  ...formik.values,
                  province: option ? option.province : "",
                  city: "",
                  district: "",
                  subdistrict: "",
                  postal_code: "",
                  detail_address: "",
                });
              }}
              getOptionLabel={(option) =>
                option.province ? option.province : "N/A"
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{ marginBottom: 2 }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <Fragment>
                        {isFetchingProvince ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </Fragment>
                    ),
                  }}
                />
              )}
            />
          </div>
          <div>
            <p>Kota</p>
            <Autocomplete
              fullWidth
              loading={isFetchingCity}
              disablePortal
              disabled={
                formik.values.province === "" || !dataCity || isFetchingAddress
              }
              options={dataCity ? dataCity : []}
              value={
                formik.values.city !== "" && dataCity
                  ? dataCity.find((city) => city.city === formik.values.city)
                  : null
              }
              onChange={(_, option) => {
                formik.setValues({
                  ...formik.values,
                  city: option ? option.city : "",
                  district: "",
                  subdistrict: "",
                  postal_code: "",
                  detail_address: "",
                });
              }}
              getOptionLabel={(option) => (option.city ? option.city : "N/A")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{ marginBottom: 2 }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <Fragment>
                        {isFetchingCity ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </Fragment>
                    ),
                  }}
                />
              )}
            />
          </div>
          <div>
            <p>District</p>
            <Autocomplete
              fullWidth
              loading={isFetchingDistrict}
              disablePortal
              disabled={
                formik.values.city === "" || !dataDistrict || isFetchingAddress
              }
              options={dataDistrict ? dataDistrict : []}
              value={
                formik.values.district !== "" && dataDistrict
                  ? dataDistrict.find(
                      (district) => district.district === formik.values.district
                    )
                  : null
              }
              onChange={(_, option) => {
                formik.setValues({
                  ...formik.values,
                  district: option ? option.district : "",
                  subdistrict: "",
                  postal_code: "",
                  detail_address: "",
                });
              }}
              getOptionLabel={(option) =>
                option.district ? option.district : "N/A"
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{ marginBottom: 2 }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <Fragment>
                        {isFetchingDistrict ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </Fragment>
                    ),
                  }}
                />
              )}
            />
          </div>
          <div>
            <p>Subdistrict</p>
            <Autocomplete
              fullWidth
              loading={isFetchingSubdistrict}
              disablePortal
              disabled={
                formik.values.district === "" ||
                !dataSubdistrict ||
                isFetchingAddress
              }
              options={dataSubdistrict ? dataSubdistrict : []}
              value={
                formik.values.subdistrict !== "" && dataSubdistrict
                  ? dataSubdistrict.find(
                      (sub) => sub.sub_district === formik.values.subdistrict
                    )
                  : null
              }
              onChange={(_, option) => {
                setMarkerByPostalCode(option);
                formik.setValues({
                  ...formik.values,
                  subdistrict: option ? option.sub_district : "",
                  postal_code: option ? option.kode_pos : "",
                });
              }}
              getOptionLabel={(option) =>
                option.sub_district ? option.sub_district : "N/A"
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{ marginBottom: 2 }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <Fragment>
                        {isFetchingSubdistrict ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </Fragment>
                    ),
                  }}
                />
              )}
            />
          </div>
          <div>
            <p>Kode Pos</p>
            <TextField
              value={formik.values.postal_code}
              disabled
              fullWidth
              sx={{ marginBottom: 2 }}
            />
          </div>
          <div>
            <p>Detail Alamat</p>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={formik.values.detail_address}
              onChange={(e) =>
                formik.setFieldValue("detail_address", e.target.value)
              }
              sx={{ marginBottom: 2 }}
            />
          </div>
          <button
            type="button"
            className="border-2 rounded-lg p-2 border-[#6F955B] bg-[#6F955B] text-white hover:bg-gray-300 disabled:bg-gray-300 w-full flex gap-2 items-center justify-center mb-2"
            // disabled={Object.values(formik.values).includes("")}
            // onClick={handleSaveAddress}
          >
            Simpan
          </button>
        </div>
        <div className="w-full h-[50vh] md:h-[80vh] overflow-hidden">
          <div className="mb-4 w-full bg-white rounded-lg p-4">
            <p className="mb-2 font-bold text-[14px]">Alamat Terdaftar</p>
            <div>
              {dataAddress && !isFetchingAddress ? (
                <p>
                  {dataAddress.detail_address} <br /> {dataAddress.subdistrict},{" "}
                  {dataAddress.district}, {dataAddress.city},{" "}
                  {dataAddress.province} {dataAddress.postal_code}
                </p>
              ) : isFetchingAddress ? (
                <CircularProgress size={20} />
              ) : (
                "Tidak ada alamat terdaftar"
              )}
            </div>
          </div>
          <div className="w-full">
            <TextField
              id="change-address-search"
              fullWidth
              placeholder="Cari lokasi anda"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ marginBottom: 1 }}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                if (e.target.value.length > 0)
                  setAnchorElSearch(e.currentTarget);
              }}
              onClick={(e) => {
                if (search.length > 0) setAnchorElSearch(e.currentTarget);
              }}
            />
            <Popper
              id="change-address-search-popper"
              disablePortal
              open={Boolean(anchorElSearch)}
              anchorEl={anchorElSearch}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
              style={{ width: anchorElSearch?.clientWidth, zIndex: 1500 }}
            >
              <ClickAwayListener onClickAway={() => setAnchorElSearch(null)}>
                <div className="w-full bg-white rounded-b-md shadow-md max-h-[30vh] overflow-y-auto">
                  {results && results.length > 0 ? (
                    results.map((res) => {
                      return (
                        <div
                          id="change-address-search-result"
                          key={res.place_id}
                          className="p-2 border-b-2 cursor-pointer hover:bg-gray-100 w-full"
                          onClick={() => {
                            setSearch(res.structured_formatting.main_text);
                            setAnchorElSearch(null);
                            setMarker(res);
                          }}
                        >
                          <p className="font-bold mb-2">
                            {res.structured_formatting.main_text}
                          </p>
                          <p className="">
                            {res.structured_formatting.secondary_text}
                          </p>
                        </div>
                      );
                    })
                  ) : (
                    <div className="p-2 text-center w-full text-gray-300">
                      Lokasi tidak ditemukan
                    </div>
                  )}
                </div>
              </ClickAwayListener>
            </Popper>
          </div>
          <div className="flex justify-center items-center min-h-[40vh]">
            <div className="w-full lg:w-[40vw] h-auto mb-4 flex justify-center items-center">
              <Map
                id="google-map"
                center={latLng}
                defaultZoom={INITIAL_ZOOM}
                onCameraChanged={handleCameraChange}
                {...cameraProps}
                style={{
                  width: "100%",
                  height: "300px",
                  position: "relative",
                  background: "#fffddd",
                  borderRadius: "12px",
                  overflow: "hidden",
                }}
              >
                <Marker position={latLng} />
              </Map>
            </div>
          </div>
        </div>
      </div>

      <ErrorPopup
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
      />
    </div>
  );
};

export default ChangeAddress;
